import { computed, onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useDeviceStore } from '@/stores'
import { defaultReportError } from '@/composables/useErrorLogger'
import { toast } from '@rapoo/element'
import type {
  CalibrationSaveType,
  CalibrationStatus,
  IMouseCalibrationFeature,
  RtSide,
} from '@rapoo/device-sdk'

/**
 * RT 校准流程（参考 SDK 协议 0xB0~0xB4）：
 * 1. 点击「开始校准」 → 调用 enterCalibration (0xB0) 进入校准模式
 * 2. 周期调用 readCalibrationStatus (0xB4) 读取左右键采集状态
 *    - CalibrationStatus.flag bit1/bit2 表示左/右键是否通过
 *    - bit3/bit4 表示左/右键是否已触发
 * 3. 左右键全部通过（bit1=1 且 bit2=1）后等待用户确认，再调用
 *    exitCalibrationAndSave (0xB2) 保存并退出校准模式
 * 4. 用户也可随时点「取消」走 exitCalibrationWithoutSave (0xB1) 不保存退出
 */

const CALIBRATION_POLL_INTERVAL_MS = 100

/** 校准状态机阶段 */
export type CalibrationPhase =
  | 'idle' // 初始 / 已结束（可重新开始）
  | 'entering' // 调用 enterCalibration 中
  | 'running' // 校准模式中，周期性读取 status
  | 'saving' // 调用 exitCalibrationAndSave 中
  | 'done' // 校准成功结束
  | 'cancelled' // 用户取消
  | 'failed' // 进入/保存过程出错

export function useMouseCalibration() {
  const { t } = useI18n()
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)

  const feature = computed<IMouseCalibrationFeature | undefined>(
    () => currentDevice.value?.features?.mouseCalibration,
  )

  const phase = ref<CalibrationPhase>('idle')
  /** 最近一次读到的原始校准状态（保留以便未来展示 ADC/LTA 等数据） */
  const status = ref<CalibrationStatus | undefined>()

  /** 单侧状态位直接从 SDK 解析后的 flag 取 */
  const leftPassed = computed(() => status.value?.flag.leftPassed ?? false)
  const rightPassed = computed(() => status.value?.flag.rightPassed ?? false)
  const leftTriggered = computed(() => status.value?.flag.leftTriggered ?? false)
  const rightTriggered = computed(() => status.value?.flag.rightTriggered ?? false)

  let pollTimer: ReturnType<typeof setInterval> | undefined
  let calibrationSession = 0
  let enterPromise: Promise<void> | undefined

  function stopPolling() {
    if (pollTimer !== undefined) {
      clearInterval(pollTimer)
      pollTimer = undefined
    }
  }

  /** 单次读取：失败仅记日志，轮询继续下一次（设备可能瞬时忙） */
  async function pollOnce() {
    const f = feature.value
    if (!f) return
    try {
      const s = await f.readCalibrationStatus()
      status.value = s
      console.log('[RT-Cal] poll', {
        flag: s.flag,
        left: s.left,
        right: s.right,
      })
    } catch (err) {
      defaultReportError(`Failed to read calibration status: ${JSON.stringify(err)}`)
    }
  }

  function startPolling() {
    if (pollTimer !== undefined) return
    void pollOnce()
    pollTimer = setInterval(() => {
      void pollOnce()
    }, CALIBRATION_POLL_INTERVAL_MS)
  }

  /**
   * 步骤 1：进入校准模式。
   * 进入后立刻开启 0xB4 轮询；左右均通过后继续保留状态，等待用户确认保存。
   */
  async function start() {
    const f = feature.value
    if (!f) {
      toast.error(t('rt.errors.calibrationNotSupported'))
      phase.value = 'failed'
      return
    }
    status.value = undefined
    phase.value = 'entering'
    const session = ++calibrationSession
    const request = f.enterCalibration()
    enterPromise = request
    try {
      await request
      // 进入请求期间可能已经切走 Tab 或点击取消。旧会话不得重新启动轮询。
      if (session !== calibrationSession) return
      console.log('[RT-Cal] entered calibration mode (0xB0)')
      phase.value = 'running'
      startPolling()
    } catch (err) {
      if (session !== calibrationSession) return
      defaultReportError(`Failed to enter calibration: ${JSON.stringify(err)}`)
      toast.error(t('rt.errors.enterCalibrationFailed'))
      phase.value = 'failed'
    } finally {
      if (enterPromise === request) enterPromise = undefined
    }
  }

  /**
   * 步骤 3：保存并退出校准模式。
   * 默认使用用户校准类型（saveType=1），可在调用处覆盖。
   */
  async function save(saveType: CalibrationSaveType = 1): Promise<boolean> {
    const f = feature.value
    if (!f) return false
    stopPolling()
    calibrationSession++
    phase.value = 'saving'
    try {
      console.log('[RT-Cal] exitCalibrationAndSave (0xB2)', { saveType })
      await f.exitCalibrationAndSave(saveType)
      console.log('[RT-Cal] calibration saved')
      phase.value = 'done'
      return true
    } catch (err) {
      defaultReportError(`Failed to save calibration: ${JSON.stringify(err)}`)
      toast.error(t('rt.errors.saveCalibrationFailed'))
      phase.value = 'failed'
      return false
    }
  }

  /**
   * 步骤 4（替代路径）：不保存直接退出。
   * 校准中随时可调用；其他阶段调用等同于 reset。
   */
  async function cancel() {
    const f = feature.value
    stopPolling()
    const currentPhase = phase.value
    const pendingEnter = enterPromise
    calibrationSession++
    if (!f) {
      phase.value = 'cancelled'
      return
    }
    if (currentPhase !== 'running' && currentPhase !== 'entering') {
      phase.value = 'cancelled'
      return
    }
    // 先更新 UI 并封住重复操作；若 0xB0 尚未完成，则等它结束后再发 0xB1，
    // 确保离开 Tab 时设备最终一定处于非校准模式。
    phase.value = 'cancelled'
    try {
      if (currentPhase === 'entering' && pendingEnter) {
        try {
          await pendingEnter
        } catch {
          return
        }
      }
      console.log('[RT-Cal] exitCalibrationWithoutSave (0xB1)')
      await f.exitCalibrationWithoutSave()
    } catch (err) {
      defaultReportError(`Failed to exit calibration without save: ${JSON.stringify(err)}`)
    } finally {
      phase.value = 'cancelled'
    }
  }

  /** 重置回 idle；用于「重新校准」或弹窗关闭后清理 */
  function reset() {
    stopPolling()
    phase.value = 'idle'
    status.value = undefined
  }

  /** 给模板用的辅助：单侧状态汇总 */
  function sideState(side: RtSide) {
    return side === 'left'
      ? { passed: leftPassed, triggered: leftTriggered }
      : { passed: rightPassed, triggered: rightTriggered }
  }

  onBeforeUnmount(() => {
    // 组件卸载时若仍在校准模式，主动走「不保存退出」避免设备被锁在校准态
    const p = phase.value
    if (p === 'running' || p === 'entering') {
      void cancel()
    } else {
      stopPolling()
    }
  })

  return {
    // 状态
    phase,
    status,
    leftPassed,
    rightPassed,
    leftTriggered,
    rightTriggered,
    // 操作
    start,
    save,
    cancel,
    reset,
    sideState,
  }
}
